import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FormRow, FormSection } from "@/components/blocks/FormRows"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ProfileSubNav } from "./ProfileSubNav"

import type { PageProps } from "@/types/inertia"

export default function ProfilePassword() {
  const intl = useIntl()
  const { props } = usePage<PageProps<{ has_password: boolean }>>()
  const hasPassword = props.has_password
  const errors = props.errors ?? {}

  const passwordForm = useForm({ current_password: "", password: "" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    passwordForm.patch("/profile/password", {
      preserveScroll: true,
      onSuccess: () => passwordForm.reset(),
    })
  }

  return (
    <>
      <Head title={intl.formatMessage({ id: "profile.password_title" })}>
        <meta name="description" content={intl.formatMessage({ id: "profile.password_meta" })} />
        <meta property="og:title" content={intl.formatMessage({ id: "profile.password_title" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "profile.password_meta" })} />
      </Head>
      <AppShell>
        <PageHeader
          title={intl.formatMessage({ id: "profile.title" })}
          description={intl.formatMessage({ id: "profile.subtitle" })}
          tabs={<ProfileSubNav active="password" />}
        />

        <form onSubmit={submit} className="mt-10 max-w-3xl">
          <FormSection
            title={intl.formatMessage({ id: "profile.password_title" })}
            description={
              hasPassword
                ? intl.formatMessage({ id: "profile.password_description_has" })
                : intl.formatMessage({ id: "profile.password_description_none" })
            }
          >
            {hasPassword && (
              <FormRow label={intl.formatMessage({ id: "profile.current_password" })} htmlFor="current_password">
                <Input
                  id="current_password"
                  type="password"
                  autoComplete="current-password"
                  required
                  aria-invalid={!!errors.current_password}
                  value={passwordForm.data.current_password}
                  onChange={(e) => passwordForm.setData("current_password", e.target.value)}
                  className="sm:max-w-md"
                />
                {errors.current_password && (
                  <p className="mt-2 text-xs text-danger-display">{errors.current_password}</p>
                )}
              </FormRow>
            )}
            <FormRow label={hasPassword ? intl.formatMessage({ id: "profile.new_password" }) : intl.formatMessage({ id: "profile.password_label" })} htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                aria-invalid={!!errors.password}
                value={passwordForm.data.password}
                onChange={(e) => passwordForm.setData("password", e.target.value)}
                className="sm:max-w-md"
              />
              {errors.password && <p className="mt-2 text-xs text-danger-display">{errors.password}</p>}
            </FormRow>
          </FormSection>
          <div className="mt-6 flex justify-end">
            <Button type="submit" disabled={passwordForm.processing}>
              {hasPassword ? intl.formatMessage({ id: "profile.update_password" }) : intl.formatMessage({ id: "profile.set_password" })}
            </Button>
          </div>
        </form>
      </AppShell>
    </>
  )
}
